import { Injectable } from '@angular/core';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ModalState {
  isVisible: boolean;
  title: string;
  message: string;
  confirmAction?: () => void;
  cancelAction?: () => void;
}

export interface StepperState {
  currentStep: number;
  totalSteps: number;
  canProceed: boolean;
  canGoBack: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UiStateService {
  constructor() {}

  /**
   * Create a validation error object
   * @param field - Field name that has the error
   * @param message - Error message
   * @returns ValidationError object
   */
  createValidationError(field: string, message: string): ValidationError {
    return { field, message };
  }

  /**
   * Format validation errors for display
   * @param errors - Array of validation errors
   * @returns Formatted error messages
   */
  formatValidationErrors(errors: ValidationError[]): string[] {
    return errors.map((error) => `${error.field}: ${error.message}`);
  }

  /**
   * Create a modal state object
   * @param title - Modal title
   * @param message - Modal message
   * @param confirmAction - Action to execute on confirm
   * @param cancelAction - Action to execute on cancel
   * @returns ModalState object
   */
  createModalState(
    title: string,
    message: string,
    confirmAction?: () => void,
    cancelAction?: () => void
  ): ModalState {
    return {
      isVisible: true,
      title,
      message,
      confirmAction,
      cancelAction,
    };
  }

  /**
   * Create a delete confirmation modal state
   * @param itemName - Name of item to delete
   * @param deleteAction - Action to execute on confirmation
   * @returns ModalState object for deletion
   */
  createDeleteConfirmationModal(
    itemName: string,
    deleteAction: () => void
  ): ModalState {
    return this.createModalState(
      'Confirm Deletion',
      `Are you sure you want to delete ${itemName}? This action cannot be undone.`,
      deleteAction
    );
  }

  /**
   * Create a bulk delete confirmation modal state
   * @param count - Number of items to delete
   * @param deleteAction - Action to execute on confirmation
   * @returns ModalState object for bulk deletion
   */
  createBulkDeleteConfirmationModal(
    count: number,
    deleteAction: () => void
  ): ModalState {
    const itemText = count === 1 ? 'item' : 'items';
    return this.createModalState(
      'Confirm Bulk Deletion',
      `Are you sure you want to delete ${count} ${itemText}? This action cannot be undone.`,
      deleteAction
    );
  }

  /**
   * Create an empty dates warning modal state
   * @param emptyDatesCount - Number of empty dates
   * @param proceedAction - Action to execute on proceed
   * @returns ModalState object for empty dates warning
   */
  createEmptyDatesWarningModal(
    emptyDatesCount: number,
    proceedAction: () => void
  ): ModalState {
    const dateText = emptyDatesCount === 1 ? 'date has' : 'dates have';
    return this.createModalState(
      'Empty Dates Warning',
      `${emptyDatesCount} ${dateText} no exercises assigned. Do you want to proceed anyway?`,
      proceedAction
    );
  }

  /**
   * Create a success modal state
   * @param message - Success message
   * @param continueAction - Action to execute on continue
   * @returns ModalState object for success
   */
  createSuccessModal(message: string, continueAction?: () => void): ModalState {
    return this.createModalState('Success', message, continueAction);
  }

  /**
   * Create stepper state object
   * @param currentStep - Current step number (0-based)
   * @param totalSteps - Total number of steps
   * @param canProceed - Whether user can proceed to next step
   * @param canGoBack - Whether user can go back to previous step
   * @returns StepperState object
   */
  createStepperState(
    currentStep: number,
    totalSteps: number,
    canProceed: boolean = true,
    canGoBack: boolean = true
  ): StepperState {
    return {
      currentStep,
      totalSteps,
      canProceed: canProceed && currentStep < totalSteps - 1,
      canGoBack: canGoBack && currentStep > 0,
    };
  }

  /**
   * Update stepper state for navigation
   * @param currentState - Current stepper state
   * @param direction - Navigation direction ('next' or 'previous')
   * @returns Updated stepper state
   */
  updateStepperState(
    currentState: StepperState,
    direction: 'next' | 'previous'
  ): StepperState {
    let newStep = currentState.currentStep;

    if (direction === 'next' && currentState.canProceed) {
      newStep = Math.min(
        currentState.totalSteps - 1,
        currentState.currentStep + 1
      );
    } else if (direction === 'previous' && currentState.canGoBack) {
      newStep = Math.max(0, currentState.currentStep - 1);
    }

    return {
      ...currentState,
      currentStep: newStep,
      canProceed: newStep < currentState.totalSteps - 1,
      canGoBack: newStep > 0,
    };
  }

  /**
   * Check if a step is accessible based on current state
   * @param targetStep - Step to check access for
   * @param currentState - Current stepper state
   * @param completedSteps - Array of completed step indices
   * @returns Whether the step is accessible
   */
  isStepAccessible(
    targetStep: number,
    currentState: StepperState,
    completedSteps: number[] = []
  ): boolean {
    // Can always access current step or previous steps
    if (targetStep <= currentState.currentStep) {
      return true;
    }

    // Can access next step only if current step is completed
    if (targetStep === currentState.currentStep + 1) {
      return completedSteps.includes(currentState.currentStep);
    }

    return false;
  }

  /**
   * Generate step navigation classes for UI styling
   * @param stepIndex - Index of the step
   * @param currentState - Current stepper state
   * @param completedSteps - Array of completed step indices
   * @returns CSS classes for the step
   */
  getStepClasses(
    stepIndex: number,
    currentState: StepperState,
    completedSteps: number[] = []
  ): string[] {
    const classes: string[] = [];

    if (stepIndex === currentState.currentStep) {
      classes.push('active', 'current');
    }

    if (stepIndex < currentState.currentStep) {
      classes.push('completed', 'accessible');
    }

    if (completedSteps.includes(stepIndex)) {
      classes.push('completed');
    }

    if (this.isStepAccessible(stepIndex, currentState, completedSteps)) {
      classes.push('accessible');
    } else {
      classes.push('disabled');
    }

    return classes;
  }

  /**
   * Create accordion state for date items
   * @param totalItems - Total number of accordion items
   * @param expandedItems - Array of expanded item indices
   * @returns Object with accordion state methods
   */
  createAccordionState(totalItems: number, expandedItems: number[] = []) {
    return {
      expandedItems: new Set(expandedItems),

      isExpanded: (index: number): boolean => {
        return expandedItems.includes(index);
      },

      toggle: (index: number): Set<number> => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(index)) {
          newExpanded.delete(index);
        } else {
          newExpanded.add(index);
        }
        return newExpanded;
      },

      expandAll: (): Set<number> => {
        return new Set(Array.from({ length: totalItems }, (_, i) => i));
      },

      collapseAll: (): Set<number> => {
        return new Set();
      },
    };
  }

  /**
   * Create loading state object
   * @param isLoading - Whether loading is in progress
   * @param message - Loading message
   * @returns Loading state object
   */
  createLoadingState(
    isLoading: boolean = false,
    message: string = 'Loading...'
  ): { isLoading: boolean; message: string } {
    return { isLoading, message };
  }

  /**
   * Create pagination state object
   * @param currentPage - Current page number (1-based)
   * @param totalItems - Total number of items
   * @param itemsPerPage - Items per page
   * @returns Pagination state object
   */
  createPaginationState(
    currentPage: number,
    totalItems: number,
    itemsPerPage: number
  ) {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startIndex,
      endIndex,
      hasNext: currentPage < totalPages,
      hasPrevious: currentPage > 1,

      getPageItems: <T>(items: T[]): T[] => {
        return items.slice(startIndex, endIndex);
      },

      goToPage: (page: number) => {
        const validPage = Math.max(1, Math.min(totalPages, page));
        return this.createPaginationState(validPage, totalItems, itemsPerPage);
      },
    };
  }
}
